import {
  APICallState,
  LottieView,
  PanelSeparator,
  Selectors,
  StandardContainer,
  Toast,
  ToastMessageContentItemType,
  ToastType,
  useIsDesktop,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';

import {RouteProp, useRoute} from '@react-navigation/native';
import {View} from 'native-base';
import React, {useEffect, useMemo, useState, useCallback} from 'react';

import PageTemplate from 'components/PageTemplate';
import {useGetOffers} from 'hooks/useGetOffers';
import {usePageViewEvent} from 'hooks/usePageViewEvent';

import ConnectedFeaturedOffersModule from 'containers/ConnectedFeaturedOffersModule';
import ConnectedRewardsSummaryModule from 'containers/ConnectedRewardsSummaryModule';
import ConnectedOffersOverlay from 'containers/ConnectedOffersOverlay';
import ConnectedReservationsModule from 'containers/ConnectedReservationsModule';
import {useDispatch, useSelector} from 'Provider';
import {
  reservationsFailedSelector,
  reservationsLoadingSelector,
} from 'store/reservations/reservations.selectors';
import {
  propertiesErrorSelector,
  propertiesLoadingSelector,
} from 'store/properties/properties.selectors';
import {
  hasNewOffersSelector,
  offersFailedSelector,
  offersLastQueryTimeSelector,
  offersLoadingSelector,
} from 'store/offers/offers.selectors';
import {LOADER_ANIMATION} from 'assets/LottieAnimations';
import {useGetReservations} from 'hooks/useGetReservations';
import {OffersActions, RefreshOffersAction} from 'store/offers/offers.reducer';
import ConnectedBreadCardModule from 'containers/ConnectedBreadCardModule';
import {useBreadSDK} from 'hooks/useBreadSDK';
import MarTechContentContainer from 'components/MarTechContentContainer';

export enum HomeScreenTabs {
  MyRewards = 'MY REWARDS',
  Account = 'ACCOUNT',
  Offers = 'OFFERS',
}

type HomeScreenParamList = {
  Home: {msg: string};
};

const HomeScreen: React.FunctionComponent = () => {
  /**
   * Get Offers and Reservations
   */
  useGetOffers();
  useGetReservations();
  /**
   *
   */

  const {trackPageViewEvent} = usePageViewEvent();
  const dispatch = useDispatch();

  const isDesktop = useIsDesktop();
  const route = useRoute<RouteProp<HomeScreenParamList, 'Home'>>();

  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector);

  const isNewUser = route.params?.msg === 'newaccount';
  const isReservationsLoading = useSelector(reservationsLoadingSelector);
  const isPropertiesLoading = useSelector(propertiesLoadingSelector);
  const isOffersLoading = useSelector(offersLoadingSelector);

  const reservationFailed = useSelector(reservationsFailedSelector);
  const propertiesFailed = useSelector(propertiesErrorSelector);
  const offersFailed = useSelector(offersFailedSelector);

  const offersLastQueryTime = useSelector(offersLastQueryTimeSelector);
  const isFirstLoad = !offersLastQueryTime?.ocrm && !offersLastQueryTime?.all;
  const hasNewOffers = useSelector(hasNewOffersSelector);
  const authenticationState = useWidgetState(
    Selectors.authenticationStateSelector
  ) as APICallState | undefined;
  const {apply} = useBreadSDK(true, false);
  const [isApplying, setIsApplying] = useState(false);

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      await apply();
    } catch (error) {
      console.error('Card application failed:', error);
    } finally {
      setIsApplying(false);
    }
  }, [apply]);

  const isLoading = useMemo(() => {
    const isReservationComponentLoading =
      isReservationsLoading || isPropertiesLoading;
    const isAuthenticationStateLoading =
      authenticationState === APICallState.LOADING;

    if (isFirstLoad) {
      return (
        isReservationComponentLoading ||
        isOffersLoading ||
        isAuthenticationStateLoading
      );
    }
    return isReservationComponentLoading || isAuthenticationStateLoading;
  }, [
    isReservationsLoading,
    isPropertiesLoading,
    isOffersLoading,
    isFirstLoad,
    authenticationState,
  ]);

  useEffect(() => {
    if (hasNewOffers) {
      dispatch(OffersActions.refreshOffers(RefreshOffersAction.refresh));
    }
  }, [hasNewOffers, dispatch]);

  useEffect(() => {
    trackPageViewEvent();
  }, []);

  const homePageContents = (
    <PageTemplate>
      {isLoggedIn ? (
        <>
          <StandardContainer>
            <View mt="48px">
              <View
                alignSelf="center"
                flexDirection="row"
                width="100%"
                justifyContent="center">
                {isLoading ? (
                  <LottieView
                    source={LOADER_ANIMATION}
                    loop
                    autoPlay
                    style={{
                      width: isDesktop ? 550 : '100%',
                      height: isDesktop ? 550 : undefined,
                    }}
                  />
                ) : (
                  <>
                    {isDesktop && (
                      <ConnectedRewardsSummaryModule
                      // tierCreditsOverride={350000} //uncomment for tier progression testing
                      />
                    )}
                    <View
                      flexDirection="column"
                      justifyContent="center"
                      flex={1}>
                      {!isDesktop && (
                        <>
                          <ConnectedRewardsSummaryModule
                          // tierCreditsOverride={350000} //uncomment for tier progression testing
                          />
                          <PanelSeparator />
                        </>
                      )}
                      {isNewUser ? (
                        <Toast
                          toast={{
                            type: ToastType.SUCCESS,
                            messageContent: [
                              {
                                elementType: ToastMessageContentItemType.TEXT,
                                text: 'Welcome to Caesars Rewards! Please complete your Caesars profile to stay up to date on the latest promotions and offers. You may now view your Caesars Rewards status, Reward Credits, and eligible offers. Play. Get rewarded.',
                              },
                            ],
                          }}
                        />
                      ) : null}
                      <>
                        <MarTechContentContainer />
                        <ConnectedBreadCardModule
                          apply={handleApply}
                          isLoading={isApplying}
                        />
                        {reservationFailed || propertiesFailed ? null : (
                          <>
                            <ConnectedReservationsModule isHomeScreen={true} />
                            <PanelSeparator />
                          </>
                        )}
                        {offersFailed ? null : (
                          <ConnectedFeaturedOffersModule />
                        )}
                      </>
                    </View>
                  </>
                )}
              </View>
            </View>
          </StandardContainer>
        </>
      ) : null}
    </PageTemplate>
  );

  return <ConnectedOffersOverlay>{homePageContents}</ConnectedOffersOverlay>;
};

export default HomeScreen;
